/**
 * BulkTargetingPage — FS-812 CSV importer for targeting rules.
 *
 * Flow:
 *   1. User pastes CSV OR picks a .csv file. Sample format hint always shown.
 *   2. Client parses preview via `parseCsv` (pure).
 *   3. Preview table shows the normalized rows (campaign / type / value / mode).
 *   4. Inline errors list per-row validation failures.
 *   5. "Import" POSTs raw CSV text to /api/v1/bulk-targeting. Backend
 *      re-parses authoritatively; UI renders the result summary.
 *
 * Brand: emerald->sky gradient headline, light theme, zinc borders, 6-8px
 * rounded, Inter. No orange/purple/dark/emojis.
 */

import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
	humanizeErrorCode,
	parseCsv,
	SAMPLE_CSV,
	type ImportResult,
	type ParseResult,
} from "./api";
import { useBulkTargetingImport, useBulkTargetingJobs } from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — match sibling features for visual consistency.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-6 shadow-[0_1px_3px_0_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const HINT = "text-xs text-zinc-500";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const TEXTAREA = `${INPUT} font-mono text-xs min-h-[160px]`;
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function BulkTargetingPage() {
	const [csvText, setCsvText] = useState("");
	const [fileName, setFileName] = useState<string | null>(null);
	const [lastResult, setLastResult] = useState<ImportResult | null>(null);
	const [fileError, setFileError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const importMut = useBulkTargetingImport();
	const jobsQuery = useBulkTargetingJobs();

	const preview: ParseResult = useMemo(() => {
		if (!csvText.trim()) {
			return { rows: [], errors: [], headerOk: false, truncated: false };
		}
		return parseCsv(csvText);
	}, [csvText]);

	const canImport =
		csvText.trim().length > 0 &&
		preview.headerOk &&
		preview.rows.length > 0 &&
		!importMut.isPending;

	const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
		setFileError(null);
		const file = e.currentTarget.files?.[0];
		if (!file) return;
		if (file.size > 2_000_000) {
			setFileError("File is over 2 MB. Split or trim before importing.");
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			const text = typeof reader.result === "string" ? reader.result : "";
			setCsvText(text);
			setFileName(file.name);
			setLastResult(null);
		};
		reader.onerror = () => {
			setFileError("Could not read file.");
		};
		reader.readAsText(file);
	};

	const handleUseSample = () => {
		setCsvText(SAMPLE_CSV);
		setFileName(null);
		setLastResult(null);
		setFileError(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleClear = () => {
		setCsvText("");
		setFileName(null);
		setLastResult(null);
		setFileError(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!canImport) return;
		setLastResult(null);
		importMut.mutate(csvText, {
			onSuccess: (res) => {
				setLastResult(res);
			},
		});
	};

	return (
		<main
			data-testid="bulk-targeting-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Heading />

			<section
				aria-labelledby="import-heading"
				className={`${CARD} flex flex-col gap-5`}
			>
				<header className="flex flex-col gap-1">
					<h2
						id="import-heading"
						className="text-lg font-semibold text-zinc-900"
					>
						Upload CSV
					</h2>
					<p className={HINT}>
						Import targeting rules in bulk. The file is parsed client-side for
						a quick preview, then posted to the API which re-validates
						authoritatively.
					</p>
				</header>

				<SampleHint onUseSample={handleUseSample} />

				<form
					onSubmit={handleSubmit}
					className="flex flex-col gap-4"
					data-testid="bulk-targeting-form"
					aria-label="bulk-targeting form"
					noValidate
				>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="bulk-targeting-file" className={LABEL}>
							Choose a .csv file
						</label>
						<input
							id="bulk-targeting-file"
							ref={fileInputRef}
							type="file"
							accept=".csv,text/csv,text/plain"
							onChange={handleFile}
							className="block w-full text-sm text-zinc-700 file:mr-3 file:rounded-[6px] file:border file:border-zinc-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-zinc-700 hover:file:bg-zinc-50"
							data-testid="bulk-targeting-file-input"
						/>
						{fileName ? (
							<p className={HINT} data-testid="bulk-targeting-filename">
								Loaded: <span className="font-mono">{fileName}</span>
							</p>
						) : null}
						{fileError ? (
							<p className="text-xs text-red-600" role="alert">
								{fileError}
							</p>
						) : null}
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="bulk-targeting-csv" className={LABEL}>
							Or paste CSV text
						</label>
						<textarea
							id="bulk-targeting-csv"
							className={TEXTAREA}
							value={csvText}
							onChange={(e) => {
								setCsvText(e.currentTarget.value);
								setLastResult(null);
							}}
							placeholder={SAMPLE_CSV}
							data-testid="bulk-targeting-csv-input"
							spellCheck={false}
						/>
					</div>

					<PreviewPanel preview={preview} hasInput={csvText.trim().length > 0} />

					<div className="flex items-center justify-end gap-2">
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={handleClear}
							disabled={importMut.isPending}
							data-testid="bulk-targeting-clear"
						>
							Clear
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={!canImport}
							data-testid="bulk-targeting-submit"
						>
							{importMut.isPending
								? "Importing."
								: `Import ${preview.rows.length} rule${preview.rows.length === 1 ? "" : "s"}`}
						</button>
					</div>

					{importMut.error ? (
						<div
							className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
							role="alert"
							data-testid="bulk-targeting-server-error"
						>
							{importMut.error.message}
						</div>
					) : null}

					{lastResult ? <ResultSummary result={lastResult} /> : null}
				</form>
			</section>

			<RecentJobs
				isLoading={jobsQuery.isLoading}
				error={jobsQuery.error}
				jobs={jobsQuery.data ?? null}
			/>
		</main>
	);
}

export default BulkTargetingPage;

// ---------------------------------------------------------------------------
// Heading
// ---------------------------------------------------------------------------

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Targeting
			</p>
			<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Bulk targeting import
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Upload a CSV of targeting rules (geo / device / OS / browser, allow or
				block) across many campaigns in one shot. Up to 5,000 rows per file.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Sample hint card
// ---------------------------------------------------------------------------

function SampleHint({ onUseSample }: { onUseSample: () => void }) {
	return (
		<div
			className="flex flex-col gap-2 rounded-[6px] border border-zinc-200 bg-zinc-50 p-4"
			data-testid="bulk-targeting-sample"
		>
			<div className="flex items-start justify-between gap-4">
				<div>
					<p className="text-sm font-medium text-zinc-800">CSV format</p>
					<p className={HINT}>
						Header row required. Columns (any order):{" "}
						<code className="rounded bg-white px-1 py-0.5 font-mono text-[11px]">
							campaign_id,type,value,mode
						</code>
					</p>
				</div>
				<button
					type="button"
					className="shrink-0 rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
					onClick={onUseSample}
					data-testid="bulk-targeting-use-sample"
				>
					Use sample
				</button>
			</div>
			<pre className="overflow-x-auto rounded-[6px] border border-zinc-200 bg-white p-3 font-mono text-[11px] text-zinc-700">
				{SAMPLE_CSV}
			</pre>
			<p className={HINT}>
				Types: <span className="font-mono">geo</span> (2-letter ISO),{" "}
				<span className="font-mono">device</span> (mobile/desktop/tablet),{" "}
				<span className="font-mono">os</span>{" "}
				(ios/android/windows/macos/linux/chromeos),{" "}
				<span className="font-mono">browser</span>{" "}
				(chrome/safari/firefox/edge/opera/samsung/ie). Mode:{" "}
				<span className="font-mono">allow</span> or{" "}
				<span className="font-mono">block</span>.
			</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Preview panel — rows table + errors list
// ---------------------------------------------------------------------------

function PreviewPanel({
	preview,
	hasInput,
}: {
	preview: ParseResult;
	hasInput: boolean;
}) {
	if (!hasInput) {
		return (
			<div
				className="rounded-[6px] border border-dashed border-zinc-300 bg-white px-3 py-6 text-center text-sm text-zinc-500"
				data-testid="bulk-targeting-preview-empty"
			>
				Pick a file or paste CSV above to preview.
			</div>
		);
	}

	if (!preview.headerOk) {
		return (
			<div
				className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700"
				role="alert"
				data-testid="bulk-targeting-header-error"
			>
				CSV header invalid. The first row must include{" "}
				<code className="font-mono text-[12px]">campaign_id</code>,{" "}
				<code className="font-mono text-[12px]">type</code>,{" "}
				<code className="font-mono text-[12px]">value</code>, and{" "}
				<code className="font-mono text-[12px]">mode</code>.
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3" data-testid="bulk-targeting-preview">
			<PreviewCounters preview={preview} />
			<PreviewTable rows={preview.rows} />
			{preview.errors.length > 0 ? (
				<ErrorList errors={preview.errors} />
			) : null}
			{preview.truncated ? (
				<p
					className="text-xs text-amber-700"
					data-testid="bulk-targeting-truncated"
				>
					Over 5,000 rows detected — only the first 5,000 will be imported.
				</p>
			) : null}
		</div>
	);
}

function PreviewCounters({ preview }: { preview: ParseResult }) {
	return (
		<div className="flex flex-wrap gap-2 text-xs">
			<Chip
				label="Rows ready"
				count={preview.rows.length}
				tone="emerald"
				testId="bulk-targeting-count-ready"
			/>
			<Chip
				label="Errors"
				count={preview.errors.length}
				tone={preview.errors.length > 0 ? "red" : "neutral"}
				testId="bulk-targeting-count-errors"
			/>
		</div>
	);
}

function Chip({
	label,
	count,
	tone,
	testId,
}: {
	label: string;
	count: number;
	tone: "neutral" | "emerald" | "red" | "amber" | "sky";
	testId?: string;
}) {
	const palette = {
		neutral: "border-zinc-200 bg-zinc-50 text-zinc-700",
		emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
		red: "border-red-200 bg-red-50 text-red-700",
		amber: "border-amber-200 bg-amber-50 text-amber-700",
		sky: "border-sky-200 bg-sky-50 text-sky-700",
	}[tone];
	return (
		<span
			className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${palette}`}
			data-testid={testId}
		>
			<span className="font-medium">{label}</span>
			<span>{count}</span>
		</span>
	);
}

function PreviewTable({
	rows,
}: {
	rows: ParseResult["rows"];
}) {
	const VISIBLE_LIMIT = 25;
	const visible = rows.slice(0, VISIBLE_LIMIT);
	const remaining = Math.max(0, rows.length - visible.length);

	return (
		<div
			className="overflow-hidden rounded-[6px] border border-zinc-200"
			data-testid="bulk-targeting-preview-table"
		>
			<table className="w-full table-fixed text-left text-sm">
				<thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
					<tr>
						<th className="w-1/3 px-3 py-2 font-medium">Campaign</th>
						<th className="w-1/6 px-3 py-2 font-medium">Type</th>
						<th className="w-1/3 px-3 py-2 font-medium">Value</th>
						<th className="w-1/6 px-3 py-2 font-medium">Mode</th>
					</tr>
				</thead>
				<tbody>
					{visible.length === 0 ? (
						<tr>
							<td
								colSpan={4}
								className="px-3 py-4 text-center text-zinc-500"
							>
								No valid rows parsed.
							</td>
						</tr>
					) : (
						visible.map((row, i) => (
							<tr
								key={`${row.campaign_id}-${row.type}-${row.value}-${row.mode}-${i}`}
								className="border-t border-zinc-200 align-top"
							>
								<td className="px-3 py-2 font-mono text-xs text-zinc-900">
									{row.campaign_id}
								</td>
								<td className="px-3 py-2 text-zinc-700">
									<TypeBadge type={row.type} />
								</td>
								<td className="px-3 py-2 font-mono text-xs text-zinc-900">
									{row.value}
								</td>
								<td className="px-3 py-2 text-zinc-700">
									<ModeBadge mode={row.mode} />
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
			{remaining > 0 ? (
				<div className="border-t border-zinc-200 bg-zinc-50 px-3 py-2 text-center text-xs text-zinc-500">
					+ {remaining} more row{remaining === 1 ? "" : "s"} (showing first{" "}
					{VISIBLE_LIMIT})
				</div>
			) : null}
		</div>
	);
}

function TypeBadge({ type }: { type: ParseResult["rows"][number]["type"] }) {
	const tone = {
		geo: "border-sky-200 bg-sky-50 text-sky-700",
		device: "border-emerald-200 bg-emerald-50 text-emerald-700",
		os: "border-zinc-200 bg-zinc-50 text-zinc-700",
		browser: "border-zinc-200 bg-zinc-50 text-zinc-700",
	}[type];
	return (
		<span
			className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.06em] ${tone}`}
		>
			{type}
		</span>
	);
}

function ModeBadge({ mode }: { mode: ParseResult["rows"][number]["mode"] }) {
	const tone =
		mode === "allow"
			? "border-emerald-200 bg-emerald-50 text-emerald-700"
			: "border-red-200 bg-red-50 text-red-700";
	return (
		<span
			className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.06em] ${tone}`}
		>
			{mode}
		</span>
	);
}

function ErrorList({ errors }: { errors: ParseResult["errors"] }) {
	const VISIBLE_LIMIT = 20;
	const visible = errors.slice(0, VISIBLE_LIMIT);
	const remaining = Math.max(0, errors.length - visible.length);

	return (
		<div
			className="flex flex-col gap-2 rounded-[6px] border border-red-200 bg-red-50 p-3"
			data-testid="bulk-targeting-errors"
		>
			<p className="text-sm font-medium text-red-700">
				{errors.length} row{errors.length === 1 ? "" : "s"} will be skipped
			</p>
			<ul className="flex flex-col gap-1 text-xs text-red-700">
				{visible.map((e, i) => (
					<li
						key={`err-${e.row}-${i}`}
						className="flex flex-col rounded-[4px] bg-white px-2 py-1.5"
					>
						<span className="font-mono text-[11px] text-zinc-500">
							Row {e.row}
						</span>
						<span className="text-zinc-800">
							{humanizeErrorCode(e.error)}
						</span>
						<span className="mt-0.5 truncate font-mono text-[11px] text-zinc-500">
							{e.raw}
						</span>
					</li>
				))}
			</ul>
			{remaining > 0 ? (
				<p className="text-xs text-red-700">
					+ {remaining} more error{remaining === 1 ? "" : "s"} (showing first{" "}
					{VISIBLE_LIMIT})
				</p>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Result summary — after successful POST
// ---------------------------------------------------------------------------

function ResultSummary({ result }: { result: ImportResult }) {
	return (
		<div
			className="flex flex-col gap-3 rounded-[6px] border border-emerald-200 bg-emerald-50 p-4"
			role="status"
			data-testid="bulk-targeting-result"
		>
			<div className="flex flex-wrap items-center gap-3 text-sm">
				<span className="font-semibold text-emerald-800">Imported.</span>
				<span className="font-mono text-xs text-emerald-800">
					{result.jobId}
				</span>
			</div>
			<div className="flex flex-wrap gap-2 text-xs">
				<Chip label="Parsed" count={result.parsed} tone="neutral" />
				<Chip label="Applied" count={result.applied} tone="emerald" />
				<Chip
					label="Skipped"
					count={result.parsed - result.applied}
					tone="neutral"
				/>
				<Chip
					label="Errors"
					count={result.errors.length}
					tone={result.errors.length > 0 ? "red" : "neutral"}
				/>
				{result.truncated ? (
					<Chip label="Truncated" count={1} tone="amber" />
				) : null}
			</div>
			{result.errors.length > 0 ? <ErrorList errors={result.errors} /> : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Recent jobs card
// ---------------------------------------------------------------------------

function RecentJobs({
	isLoading,
	error,
	jobs,
}: {
	isLoading: boolean;
	error: Error | null;
	jobs: readonly {
		id: string;
		parsed: number;
		applied: number;
		error_count: number;
		created_at: number;
	}[] | null;
}) {
	return (
		<section
			className={CARD}
			aria-labelledby="jobs-heading"
			data-testid="bulk-targeting-jobs"
		>
			<header className="mb-4 flex flex-col gap-1">
				<h2 id="jobs-heading" className="text-lg font-semibold text-zinc-900">
					Recent imports
				</h2>
				<p className={HINT}>
					Every import creates a tenant-scoped job record. Oldest first is
					never useful here — newest first.
				</p>
			</header>

			{isLoading ? (
				<p className="text-sm text-zinc-500" data-testid="bulk-targeting-loading">
					Loading recent imports.
				</p>
			) : error ? (
				<p className="text-sm text-red-600" role="alert">
					{error.message}
				</p>
			) : !jobs || jobs.length === 0 ? (
				<p
					className="text-sm text-zinc-500"
					data-testid="bulk-targeting-empty"
				>
					No imports yet. Your first upload will show up here.
				</p>
			) : (
				<div className="overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full table-fixed text-left text-sm">
						<thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
							<tr>
								<th className="w-1/2 px-3 py-2 font-medium">Job</th>
								<th className="w-1/6 px-3 py-2 font-medium">Parsed</th>
								<th className="w-1/6 px-3 py-2 font-medium">Applied</th>
								<th className="w-1/6 px-3 py-2 font-medium">Errors</th>
							</tr>
						</thead>
						<tbody>
							{jobs.map((job) => (
								<tr
									key={job.id}
									className="border-t border-zinc-200 align-top"
								>
									<td className="px-3 py-2">
										<div className="font-mono text-xs text-zinc-900">
											{job.id}
										</div>
										<div className="text-[11px] text-zinc-500">
											{formatEpochSec(job.created_at)}
										</div>
									</td>
									<td className="px-3 py-2 text-zinc-700">{job.parsed}</td>
									<td className="px-3 py-2 text-emerald-700">
										{job.applied}
									</td>
									<td
										className={
											job.error_count > 0
												? "px-3 py-2 text-red-600"
												: "px-3 py-2 text-zinc-500"
										}
									>
										{job.error_count}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function formatEpochSec(ts: number): string {
	if (!Number.isFinite(ts) || ts <= 0) return "-";
	try {
		const d = new Date(ts * 1000);
		if (Number.isNaN(d.getTime())) return "-";
		return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
	} catch {
		return "-";
	}
}
